/**
 * Admin reference-data API — the eastview-os `/admin/data/*` router
 * (`app/routers/admin_data.py`). Every endpoint is admin-gated (`require_admin`)
 * and every mutation creates a NEW immutable version stamped with the acting
 * user, with an edit log you can render and revert from.
 *
 * Four datasets live under this prefix:
 *   zhvi        — Zillow ZHVI market data (upload / ledger / ack / rollback / per-zip)
 *                 → frontend_documentation/admin-market-data-zhvi.md
 *   guidelines  — versioned guideline markdown + codified rules JSON
 *                 → frontend_documentation/guidelines-and-dscr-pricing-admin.md
 *   rtl-pricing — the additive RTL/GUC rate model (sections + live preview)
 *   dscr-pricing— versioned counterparty price sheets (import / diff / save)
 *                 → frontend_documentation/guidelines-and-dscr-pricing-admin.md
 *
 * Scale note (pricing-config-and-lock-extensions.md): RTL rates are annual
 * DECIMAL FRACTIONS (0.105 = 10.50%); DSCR margins are BOND POINTS (par 100).
 * The two never mix in one field.
 */
import { losHttp } from "../lib/losHttp";

const BASE = "/admin/data";

// The ZHVI CSV is ~120MB and the parse runs 30–60s server-side — give the
// upload its own generous timeout instead of the client default.
const ZHVI_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export const adminZhviApi = {
  // POST /admin/data/zhvi/upload (multipart `file`)
  //   → { version, rows, lastReportedDate, uploadedBy }
  upload: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return losHttp
      .post(`${BASE}/zhvi/upload`, fd, {
        timeout: ZHVI_UPLOAD_TIMEOUT_MS,
        onUploadProgress,
      })
      .then((r) => r.data);
  },

  // GET /admin/data/zhvi/versions → { dataset, versions: [...] } (newest first)
  versions: () =>
    losHttp.get(`${BASE}/zhvi/versions`).then((r) => r.data?.versions || []),

  // POST /admin/data/zhvi/versions/{versionId}/acknowledge — second-user ack
  acknowledge: (versionId) =>
    losHttp
      .post(`${BASE}/zhvi/versions/${versionId}/acknowledge`)
      .then((r) => r.data),

  // POST /admin/data/zhvi/rollback — restores the PRIOR generation (one level;
  // calling again rolls forward). 409 when there is no prior generation.
  rollback: () => losHttp.post(`${BASE}/zhvi/rollback`).then((r) => r.data),

  // GET /admin/data/zhvi/zip/{zip} → { record, assessment }. 404 when the zip
  // isn't in the active dataset.
  zip: (zipCode) =>
    losHttp.get(`${BASE}/zhvi/zip/${zipCode}`).then((r) => r.data),
};

export const adminGuidelinesApi = {
  // GET /admin/data/guidelines → manifest per slug (left rail)
  list: () =>
    losHttp.get(`${BASE}/guidelines`).then((r) => r.data?.guidelines || []),

  // GET /admin/data/guidelines/{slug}?version=N&includeRaw=
  //   → manifest + markdown (TipTap source) + structured (rules JSON) [+ agentRaw]
  get: (slug, { version, includeRaw } = {}) =>
    losHttp
      .get(`${BASE}/guidelines/${slug}`, {
        params: {
          ...(version ? { version } : {}),
          ...(includeRaw ? { includeRaw: true } : {}),
        },
      })
      .then((r) => r.data),

  // GET /admin/data/guidelines/{slug}/log → [{ at, by, action, fromVersion, toVersion, note }]
  log: (slug) =>
    losHttp
      .get(`${BASE}/guidelines/${slug}/log`)
      .then((r) => r.data?.editLog || []),

  // PUT /admin/data/guidelines/{slug}/markdown {markdown, note?, notify?, recodify?}
  saveMarkdown: (slug, body) =>
    losHttp.put(`${BASE}/guidelines/${slug}/markdown`, body).then((r) => r.data),

  // PUT /admin/data/guidelines/{slug}/structured {structured, note?, notify?}
  saveStructured: (slug, body) =>
    losHttp
      .put(`${BASE}/guidelines/${slug}/structured`, body)
      .then((r) => r.data),

  // POST /admin/data/guidelines/{slug}/codify — re-run the codify agent
  codify: (slug) =>
    losHttp
      .post(`${BASE}/guidelines/${slug}/codify`, null, { timeout: 5 * 60 * 1000 })
      .then((r) => r.data),

  // POST /admin/data/guidelines/{slug}/revert {toVersion, note?, notify?}
  revert: (slug, body) =>
    losHttp.post(`${BASE}/guidelines/${slug}/revert`, body).then((r) => r.data),

  // POST /admin/data/guidelines/import (multipart: file, title, product?, slug?, notify?, codify?)
  import: (file, { title, product, slug, notify, codify } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "");
    if (product) fd.append("product", product);
    if (slug) fd.append("slug", slug);
    fd.append("notify", notify ? "true" : "false");
    fd.append("codify", codify === false ? "false" : "true");
    return losHttp
      .post(`${BASE}/guidelines/import`, fd, { timeout: 10 * 60 * 1000 })
      .then((r) => r.data);
  },
};

// The five editable sections of the additive RTL/GUC rate model, in the order
// they compose a rate. Mirrors RtlPricingStore.SECTIONS server-side.
export const RTL_PRICING_SECTIONS = [
  "baseRates",
  "termAdjustments",
  "classAdjustments",
  "loanAmountUpticks",
  "maxLeverageAdjustments",
];

export const adminRtlPricingApi = {
  // GET /admin/data/rtl-pricing → { _id, sections: { [section]: {v, data} }, seeded }
  get: () => losHttp.get(`${BASE}/rtl-pricing`).then((r) => r.data),

  // PUT /admin/data/rtl-pricing/{section} {data, note?} → { section, version }
  saveSection: (section, data, note) =>
    losHttp
      .put(`${BASE}/rtl-pricing/${section}`, { data, note: note || "" })
      .then((r) => r.data),

  // POST /admin/data/rtl-pricing/preview — every additive component + buyRate
  preview: (body) =>
    losHttp.post(`${BASE}/rtl-pricing/preview`, body).then((r) => r.data),

  // GET /admin/data/rtl-pricing/tier-rates?shiftPct= → rate by product per
  // correspondent tier: { units, stepPct, shiftPct, tiers: {name: spreadPct},
  // rows: [{product, baseRatePct, tiers: {name: {spreadPct, ratePct}}}],
  // shifted: same with every tier spread moved by shiftPct, shiftOptions }.
  // PERCENT throughout. shiftPct must sit on the 0.125% grid within ±2% (400
  // otherwise). Preview only — saving a spread is per tier.
  tierRates: (shiftPct = 0) =>
    losHttp
      .get(`${BASE}/rtl-pricing/tier-rates`, { params: { shiftPct } })
      .then((r) => r.data),

  // GET /admin/data/rtl-pricing/log → the cross-section edit log
  log: () =>
    losHttp.get(`${BASE}/rtl-pricing/log`).then((r) => r.data?.editLog || []),

  // POST /admin/data/rtl-pricing/seed — write the code defaults into the config
  seed: () => losHttp.post(`${BASE}/rtl-pricing/seed`).then((r) => r.data),
};

export const adminDscrPricingApi = {
  // GET /admin/data/dscr-pricing → counterparty sheets, ordered for the picker:
  // the platform base sheet (`resfin-base`, owner 2026-10-08) leads, then
  // Eastview base (kept through the continuity gap), then the rest
  // alphabetically.
  list: () =>
    losHttp.get(`${BASE}/dscr-pricing`).then((r) => {
      const rows = r.data?.counterparties || [];
      const BASE_ORDER = ["resfin-base", "eastview-base"];
      return rows.slice().sort((a, b) => {
        const base = (c) => {
          const i = BASE_ORDER.indexOf(c?.cpId);
          return i < 0 ? BASE_ORDER.length : i;
        };
        return (
          base(a) - base(b) ||
          String(a?.counterparty || a?.cpId || "").localeCompare(
            String(b?.counterparty || b?.cpId || ""),
          )
        );
      });
    }),

  // GET /admin/data/dscr-pricing/{cpId}?version=N → manifest + `pricing` objects
  get: (cpId, version) =>
    losHttp
      .get(`${BASE}/dscr-pricing/${cpId}`, {
        params: version ? { version } : undefined,
      })
      .then((r) => r.data),

  // PUT /admin/data/dscr-pricing/{cpId} {pricing, counterparty?, status?, note?, notify?}
  save: (cpId, body) =>
    losHttp.put(`${BASE}/dscr-pricing/${cpId}`, body).then((r) => r.data),

  // GET /admin/data/dscr-pricing/{cpId}/days — daily sheet tracking: the days
  // this counterparty's sheet was captured (`{cpId}/daily/{day}/pricing.json`).
  // → { cpId, days: ["2026-10-08", …] newest first, latest: "<blob path>" }
  days: (cpId) =>
    losHttp.get(`${BASE}/dscr-pricing/${cpId}/days`).then((r) => r.data),

  // GET /admin/data/dscr-pricing/{cpId}/log
  log: (cpId) =>
    losHttp
      .get(`${BASE}/dscr-pricing/${cpId}/log`)
      .then((r) => r.data?.editLog || []),

  // POST /admin/data/dscr-pricing/{cpId}/revert {toVersion, note?, notify?}
  revert: (cpId, body) =>
    losHttp
      .post(`${BASE}/dscr-pricing/${cpId}/revert`, body)
      .then((r) => r.data),

  // POST /admin/data/dscr-pricing/{cpId}/agent-review?version=&store=
  // On-demand review of an ALREADY-STORED version: re-runs the grid agent on
  // that version's original workbook and the overlay agent on its pricing.
  // Gives versions imported before the agent existed the same review surface a
  // new import gets. `store` persists it as `lastAgentReview` on the
  // counterparty — NOT a new version, so it never touches the pricing.
  // → { cpId, version, sourceWorkbookAvailable, gridAgent{recognition,validation},
  //     overlaySuggestions[], overlayAgentNotes, reviewedAt }
  agentReview: (cpId, { version, store } = {}) =>
    losHttp
      .post(`${BASE}/dscr-pricing/${cpId}/agent-review`, null, {
        params: {
          ...(version != null ? { version } : null),
          ...(store ? { store: true } : null),
        },
      })
      .then((r) => r.data),

  // POST /admin/data/dscr-pricing/import (multipart) — `store=false` (default)
  // stages the parse for FE review; save it afterwards with `save()`.
  // `asOfDate` (YYYY-MM-DD) is the sheet DAY the workbook reflects (daily sheet
  // tracking); the backend defaults it to its own (UTC) today when omitted.
  import: (
    file,
    {
      counterparty,
      cpId,
      contactName,
      contactEmail,
      status,
      store,
      notify,
      asOfDate,
    } = {},
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("counterparty", counterparty || "");
    if (cpId) fd.append("cpId", cpId);
    if (contactName) fd.append("contactName", contactName);
    if (contactEmail) fd.append("contactEmail", contactEmail);
    fd.append("status", status || "draft");
    fd.append("store", store ? "true" : "false");
    fd.append("notify", notify ? "true" : "false");
    if (asOfDate) fd.append("asOfDate", asOfDate);
    return losHttp
      .post(`${BASE}/dscr-pricing/import`, fd, { timeout: 10 * 60 * 1000 })
      .then((r) => r.data);
  },

  // POST /admin/data/dscr-pricing/{cpId}/diff (multipart) — diff a NEW workbook
  // against the stored current version. Nothing is persisted.
  diff: (cpId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return losHttp
      .post(`${BASE}/dscr-pricing/${cpId}/diff`, fd, { timeout: 10 * 60 * 1000 })
      .then((r) => r.data);
  },
};

export const adminDataApi = {
  zhvi: adminZhviApi,
  guidelines: adminGuidelinesApi,
  rtlPricing: adminRtlPricingApi,
  dscrPricing: adminDscrPricingApi,
};

export default adminDataApi;
